import React from 'react';
import { useId } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { CompactRelativeTimestamp } from '@/components/CompactRelativeTimestamp';
import { UserLink } from '@/components/UserLink';
import { ASPECT_REVIEW, ASPECT_QUESTION } from '@/lib/consts';
import { DISPLAY_IMAGE_WIDTHS, getSizedImageUrl } from '@/lib/displayImage';
import { extractPlainText } from '@/lib/structuredDocUtils';
import { isLLM } from '@/lib/utils';

import {
  DisplayThread,
  DisplayDoc,
  DisplayUser,
  FeedItem,
  DocNode,
  filterFeedbackSubtrees,
} from '../lib/api';

type ThreadFeedItemProps = {
  item: FeedItem;
  showTopic: boolean;
  currentUser?: DisplayUser | null;
};

const getPreviewText = (doc: DisplayDoc, maxLength: number): string => {
  const fullText = doc.structured_content ? extractPlainText(doc.structured_content) : doc.text;
  return fullText.length > maxLength ? `${fullText.substring(0, maxLength)}...` : fullText;
};

export const ThreadFeedItem: React.FC<ThreadFeedItemProps> = ({ item, showTopic, currentUser }) => {
  const threadId = useId();

  if (!item) return null;
  const thread = item.thread;
  if (!thread || thread.docs.length == 0) return null;

  // Filter out feedback posts and their children from the thread
  const filteredDocs = filterFeedbackSubtrees(thread.docs);

  // If no docs remain after filtering, don't render this feed item
  if (filteredDocs.length === 0) return null;

  // Find the latest doc from a human user (non-LLM) from filtered docs
  const latestUserDoc = (() => {
    for (let i = filteredDocs.length - 1; i >= 0; i--) {
      const doc = filteredDocs[i];
      if (!isLLM(doc.provenance.creator)) {
        return doc;
      }
    }
    // Fallback to the last filtered doc
    return filteredDocs[filteredDocs.length - 1];
  })();

  // Is there a review or question in the filtered docs?
  const hasReview = filteredDocs.some((doc) => doc.aspect?.cref === ASPECT_REVIEW);
  const hasQuestion = filteredDocs.some((doc) => doc.aspect?.cref === ASPECT_QUESTION);

  const href_target = `/doc/${latestUserDoc.id}`;
  const topic = latestUserDoc.topic?.display || '';

  const just = item.justification;

  const otherParticipants = Array.from(new Set(thread.docs.map((doc) => doc.provenance.creator)))
    .filter((creator) => creator !== currentUser?.username)
    .filter((creator) => creator !== just?.friend)
    .filter((creator) => !isLLM(creator));

  const headerText = (): string => {
    let result: string = '';
    if (hasReview) {
      result += `Review `;
    } else if (hasQuestion) {
      result += `Question `;
    }
    // For non-logged-in users or feeds with incomplete justification
    if (!currentUser || !just) {
      if (otherParticipants.length > 0) {
        return otherParticipants.map((creator) => `@${creator}`).join(', ');
      }
      return topic || 'Thread';
    }

    // For logged-in users with justification
    if (just.mentioner) {
      result += `@${just.mentioner} tagged you`;
    } else if (just.friend) {
      result += `@${just.friend}'s thread`;
    }
    if (result.length === 0 && just.topic) {
      result += `Your interests: ${just.topic.display}`;
    }
    if (result.length === 0 && otherParticipants.length > 0) {
      result += otherParticipants.map((creator) => `@${creator}`).join(', ');
    }
    if (result.length === 0 && just.is_owned) {
      result += `Your thread`;
    }
    return result;
  };

  const renderHeader = (): React.ReactNode => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <Typography variant="body2" component="span" color="text.secondary">
        {headerText()}
      </Typography>
      <Typography variant="body2" component="span" color="text.secondary">
        {showTopic ? topic : 'Thread'}
      </Typography>
    </Box>
  );

  const renderReplyChain = (): React.JSX.Element => {
    return (
      <Box component="section" aria-label="Reply chain" sx={{ mt: 1 }}>
        <List dense disablePadding>
          {filteredDocs.slice(-5).map((doc, index) => (
            <ListItem key={index} disableGutters sx={{ display: 'block', py: 0.5 }}>
              <Typography variant="body2" component="span">
                <Typography component="span" fontWeight="bold">
                  {doc.provenance.creator}:
                </Typography>{' '}
                {getPreviewText(doc, 450)}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  const findSKUImageHref = (): string | null => {
    // Check each doc in the filtered docs, and each concept in the doc
    for (const doc of filteredDocs) {
      if (doc.topic?.preview_image) {
        return getSizedImageUrl(doc.topic.preview_image, DISPLAY_IMAGE_WIDTHS.thumbnail);
      }
      for (const concept of doc.mentioned_concepts) {
        if (concept.preview_image) {
          return getSizedImageUrl(concept.preview_image, DISPLAY_IMAGE_WIDTHS.thumbnail);
        }
      }
    }
    return null;
  };

  let skuImageHref = findSKUImageHref();

  return (
    <Card sx={{ mb: 1 }}>
      <CardActionArea
        component={Link}
        href={href_target}
        aria-label={`View ${showTopic ? topic : 'Thread'}`}
      >
        <CardHeader
          title={renderHeader()}
          sx={{
            pb: 1,
            '& .MuiCardHeader-content': {
              width: '100%',
            },
          }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Box
            component="article"
            aria-labelledby={`thread-${threadId}`}
            sx={{ display: 'flex', gap: 2 }}
          >
            <Box component={Typography} id={`thread-${threadId}`} sx={visuallyHidden}>
              Thread Content
            </Box>

            {skuImageHref && (
              <Box
                sx={{
                  position: 'relative',
                  width: 100,
                  height: 100,
                  flexShrink: 0,
                  overflow: 'hidden',
                  borderRadius: 1,
                }}
              >
                <Image
                  src={skuImageHref}
                  alt="Product Image"
                  fill
                  sizes="100px"
                  style={{ objectFit: 'cover' }}
                />
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {item.summary ? (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {item.summary.text}
                </Typography>
              ) : (
                <>
                  <Typography variant="body1" component="div">
                    <Typography component="span" fontWeight="bold">
                      {latestUserDoc.provenance.creator}
                    </Typography>{' '}
                    <CompactRelativeTimestamp timestamp={latestUserDoc.provenance.createdon} />{' '}
                    <Typography component="span" fontWeight="bold">
                      :
                    </Typography>{' '}
                    {getPreviewText(latestUserDoc, 450)}
                  </Typography>
                  {filteredDocs.length > 1 && renderReplyChain()}
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
